import { useState } from 'react';
import type { ReactNode } from 'react';

import { brandingName } from '../../api/branding';
import { publicLanguages, useBranding } from '../../config';
import { useSession } from '../../session/useSession';
import { Icon } from '../Icon';

/**
 * L'enveloppe des pages publiques et d'avant la coquille (planches 36 à 43, D41) :
 * connexion, second facteur, mot de passe oublié, lien, mot de passe imposé.
 *
 * À gauche, l'établissement : logo, nom, phrase et villes, tirés de `config.json`
 * (une instance par établissement ; avant la connexion, l'API ne dit pas encore
 * qui l'on sert). À droite, le formulaire, le choix de la langue, et la phrase
 * qui fait reconnaître un écran authentique.
 *
 * Le choix de la langue n'est proposé que tant que personne n'est connecté :
 * après, c'est celui de la barre supérieure.
 */
export function AuthShell({ children }: { readonly children: ReactNode }): ReactNode {
    const { t, session, locale } = useSession();
    const { assets: brand, server } = useBranding();
    const name = brandingName(server, locale) ?? brand.name;

    return (
        <div className="auth">
            <aside className="auth__brand">
                {brand.logo === undefined ? null : (
                    <div className="auth__logo">
                        <img src={brand.logo} alt={name ?? ''} />
                    </div>
                )}
                <div className="auth__pitch">
                    {name === undefined ? null : <span className="auth__name">{name}</span>}
                    <p className="auth__tagline">{brand.tagline ?? t('auth.tagline')}</p>
                    {brand.places === undefined ? null : <span className="auth__places">{brand.places}</span>}
                </div>
            </aside>
            <main className="auth__main">
                <div className="auth__top">{session.status === 'anonymous' ? <LanguagePicker /> : null}</div>
                <div className="auth__content">{children}</div>
                <footer className="auth__foot">
                    <span>{t('auth.vendor')}</span>
                    <span>{t('auth.never')}</span>
                </footer>
            </main>
        </div>
    );
}

/** Le nom de chaque langue dans sa propre langue, comme partout ailleurs. */
const LANGUAGE_NAMES: Readonly<Record<string, string>> = { fr: 'Français', en: 'English', ar: 'العربية' };

function LanguagePicker(): ReactNode {
    const { t, locale, chooseLocale } = useSession();

    return (
        <label className="auth__language">
            <Icon name="globe" size={16} />
            <span className="visually-hidden">{t('language.label')}</span>
            <select value={locale} onChange={(event) => void chooseLocale(event.target.value)}>
                {publicLanguages().map((code) => (
                    <option key={code} value={code} lang={code}>
                        {LANGUAGE_NAMES[code] ?? code}
                    </option>
                ))}
            </select>
        </label>
    );
}

/** La longueur maximale d'un mot de passe (PasswordRules::MAXIMUM_LENGTH côté serveur). */
const PASSWORD_MAX = 4096;

/** Un champ de mot de passe avec son bouton « afficher ». */
export function PasswordInput({
    id,
    value,
    onChange,
    autoComplete,
    invalid = false,
    describedBy,
    name,
}: {
    readonly id: string;
    readonly value: string;
    onChange(value: string): void;
    readonly autoComplete: 'current-password' | 'new-password';
    readonly invalid?: boolean;
    readonly describedBy?: string;
    readonly name?: string;
}): ReactNode {
    const { t } = useSession();
    const [shown, setShown] = useState(false);

    return (
        <span className={`password-input${invalid ? ' password-input--invalid' : ''}`}>
            <input
                id={id}
                name={name}
                className="field__input"
                type={shown ? 'text' : 'password'}
                autoComplete={autoComplete}
                required
                aria-required={true}
                maxLength={PASSWORD_MAX}
                value={value}
                aria-invalid={invalid ? true : undefined}
                aria-describedby={describedBy}
                onChange={(event) => onChange(event.target.value)}
            />
            <button className="password-input__toggle" type="button" aria-label={shown ? t('auth.hidePassword') : t('auth.showPassword')} aria-pressed={shown} onClick={() => setShown((value) => !value)}>
                <Icon name={shown ? 'eyeOff' : 'eye'} size={18} />
            </button>
        </span>
    );
}

/** Une jauge simple : la longueur, seule règle (12 caractères, NIST SP 800-63B). */
export function PasswordStrength({ value }: { readonly value: string }): ReactNode {
    const { t } = useSession();
    const length = value.length;
    const level = length === 0 ? 0 : length < 12 ? 1 : length < 16 ? 2 : length < 20 ? 3 : 4;

    return (
        <div className="password-strength">
            <div className={`password-strength__bar password-strength__bar--${level}`} aria-hidden="true">
                <span />
                <span />
                <span />
                <span />
            </div>
            <ul className="password-strength__rules">
                <li className={length >= 12 ? 'password-strength__rule--ok' : undefined}>
                    <Icon name={length >= 12 ? 'check' : 'info'} size={15} />
                    {t('auth.rule.length', { count: length })}
                </li>
                <li>
                    <Icon name="info" size={15} />
                    {t('auth.rule.sentence')}
                </li>
            </ul>
        </div>
    );
}
